import { z } from "zod";
import { readBackendConfig } from "@/lib/backend-config";
import { StaffSessionError } from "../approval";
import type { AuthProfile, StaffSessionResult } from "../types";

const STAFF_AUTH_FUNCTION = "staff-auth";
const CSRF_HEADER = "x-xan-staff-auth";
const TRAILING_SLASH = /\/$/;

export interface StaffAuthUser {
  readonly email?: string;
  readonly id: string;
  readonly phone?: string;
  readonly profile_id: string;
  readonly role: string;
}

export interface StaffAuthSessionResponse {
  readonly access_token: string;
  readonly expires_at: number;
  readonly profile: AuthProfile;
  readonly token_type: "bearer";
  readonly user: StaffAuthUser;
}

/** Error returned by the staff-auth edge function with its HTTP status. */
export class StaffAuthClientError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "StaffAuthClientError";
    this.status = status;
  }
}

const StaffAuthProfileSchema = z.object({
  approval_status: z.string().nullable().default(null),
  auth_user_id: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  full_name: z.string().nullable().default(null),
  id: z.string(),
  is_active: z.boolean().nullable().default(null),
  phone: z.string().nullable().default(null),
  role: z.string().nullable().default(null),
});

const StaffAuthUserSchema = z.object({
  email: z.string().optional(),
  id: z.string(),
  phone: z.string().optional(),
  profile_id: z.string(),
  role: z.string(),
});

const StaffAuthSessionResponseSchema = z.object({
  access_token: z.string(),
  expires_at: z.number(),
  profile: StaffAuthProfileSchema,
  token_type: z.literal("bearer"),
  user: StaffAuthUserSchema,
});

const SuccessSchema = z.object({ success: z.literal(true) });

function staffAuthUrl(): string {
  const { url } = readBackendConfig();
  return `${url.replace(TRAILING_SLASH, "")}/api/functions/${STAFF_AUTH_FUNCTION}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function parseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function invokeStaffAuth<T>(body: Record<string, unknown>, schema: z.ZodType<T>): Promise<T> {
  const response = await fetch(staffAuthUrl(), {
    body: JSON.stringify(body),
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      [CSRF_HEADER]: "1",
    },
    method: "POST",
  });
  const payload = await parseJson(response);
  if (!response.ok) {
    const message =
      isRecord(payload) && typeof payload.error === "string"
        ? payload.error
        : "Authentication request failed";
    throw new StaffAuthClientError(message, response.status);
  }
  return schema.parse(payload);
}

/** Requests a staff SMS verification code from the staff-auth edge function. */
export function sendStaffSmsCode(phone: string): Promise<{ success: true }> {
  return invokeStaffAuth(
    {
      action: "sms.send",
      phone,
    },
    SuccessSchema,
  );
}

/** Verifies an SMS code and returns the staff-auth edge session payload. */
export function verifyStaffSmsCode(input: {
  readonly code: string;
  readonly phone: string;
}): Promise<StaffAuthSessionResponse> {
  return invokeStaffAuth(
    {
      action: "sms.verify",
      code: input.code,
      phone: input.phone,
    },
    StaffAuthSessionResponseSchema,
  );
}

/** Normalize an SMS edge session response into the shared staff-session shape. */
export function staffAuthSessionToResult(response: StaffAuthSessionResponse): StaffSessionResult {
  const authUserId = response.profile.auth_user_id;
  if (!authUserId || authUserId !== response.user.id) {
    throw new StaffSessionError(
      "session_invalid",
      "We couldn't load your staff session. Try again.",
    );
  }
  return {
    accessToken: response.access_token,
    expiresAt: response.expires_at,
    origin: "sms",
    profile: response.profile,
    user: {
      email: response.user.email,
      id: authUserId,
      phone: response.user.phone,
      role: response.user.role,
    },
  };
}
